import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { Chip, formatNumber, Table } from "@narmi/design_system";
import Filters from "byzantine/src/filters";
import Transaction from "byzantine/src/Transaction";
import MerchantIcon from "../../subscriptions/MerchantIcon/MerchantIcon";
import TransactionDetailsDialog from "../../TransactionDetails";
import NewThreadDialog from "../../NewThreadDialog";
import styles from "./RecentTransactionsModule.module.scss";

interface TransactionRowProps {
  transaction: Transaction;
  replyTime: string;
}

const useTransactionRowState = (transaction: Transaction) => {
  const {
    amount,
    resulting_balance: resultingBalance,
    posted_date: postedDate,
    creation_date: creationDate,
    description,
    account,
  } = transaction;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [displayDescription, setDisplayDescription] = useState(description);
  const [isReportIssueDialogOpen, setIsReportIssueDialogOpen] = useState(false);

  useEffect(() => {
    setDisplayDescription(description);
  }, [description]);

  const isPositive = amount > 0;
  const displayedAmount = `${isPositive ? "+" : ""}${formatNumber(
    amount / 100,
    "currency"
  )}`;
  const displayedResultingBalance =
    !postedDate || resultingBalance == null
      ? undefined
      : formatNumber((resultingBalance as number) / 100);
  const accountLabel = account?.nickname || account?.name || "";
  const dateLabel = Filters.shortMonthDay(postedDate || creationDate);
  const isPending = !postedDate;

  return {
    isDialogOpen,
    setIsDialogOpen,
    displayDescription,
    setDisplayDescription,
    isReportIssueDialogOpen,
    setIsReportIssueDialogOpen,
    isPositive,
    displayedAmount,
    displayedResultingBalance,
    accountLabel,
    dateLabel,
    isPending,
  };
};

interface TransactionDialogsProps {
  transaction: Transaction;
  replyTime: string;
  displayDescription: string;
  displayedAmount: string;
  displayedResultingBalance: string | undefined;
  isDialogOpen: boolean;
  closeDialog: () => void;
  onDescriptionUpdate: (description: string) => void;
  isReportIssueDialogOpen: boolean;
  closeReportIssueDialog: () => void;
  openReportIssueDialog: () => void;
}

const TransactionDialogs = ({
  transaction,
  replyTime,
  displayDescription,
  displayedAmount,
  displayedResultingBalance,
  isDialogOpen,
  closeDialog,
  onDescriptionUpdate,
  isReportIssueDialogOpen,
  closeReportIssueDialog,
  openReportIssueDialog,
}: TransactionDialogsProps) => {
  const { l10n } = useLocalization();
  const { isPending } = transaction;
  const transactionTitle = (
    <div className={styles.dialogTitle}>
      {displayDescription}
      {isPending && (
        <Chip
          label={l10n.getString("transaction-status-pending")}
          kind="secondary"
        />
      )}
    </div>
  );

  return (
    <>
      <TransactionDetailsDialog
        transaction={transaction}
        transactionTitle={transactionTitle}
        displayedAmount={displayedAmount}
        displayedResultingBalance={displayedResultingBalance}
        isSourceMultipleAccounts
        isDialogOpen={isDialogOpen}
        closeDialog={closeDialog}
        onDescriptionUpdate={onDescriptionUpdate}
        ReportIssueCallback={openReportIssueDialog}
      />
      <NewThreadDialog
        accountNumber={transaction.account?.number}
        transactionId={transaction.id}
        isDialogOpen={isReportIssueDialogOpen}
        closeDialog={closeReportIssueDialog}
        replyTime={replyTime}
      />
    </>
  );
};

// Tablet button row — rendered inside .tabletList
export const TransactionTabletRow = ({
  transaction,
  replyTime,
}: TransactionRowProps) => {
  const { l10n } = useLocalization();
  const {
    isDialogOpen,
    setIsDialogOpen,
    displayDescription,
    setDisplayDescription,
    isReportIssueDialogOpen,
    setIsReportIssueDialogOpen,
    isPositive,
    displayedAmount,
    displayedResultingBalance,
    accountLabel,
    dateLabel,
    isPending,
  } = useTransactionRowState(transaction);

  return (
    <>
      <button
        type="button"
        className={`${styles.tabletRow} button--reset`}
        onClick={() => setIsDialogOpen(true)}
      >
        <div className={styles.tabletRowMain}>
          <div className={styles.tabletRowDescription}>
            {displayDescription}
            {isPending && (
              <Chip
                label={l10n.getString("transaction-status-pending")}
                kind="secondary"
              />
            )}
          </div>
          <span className={isPositive ? styles.amountPositive : styles.amount}>
            {displayedAmount}
          </span>
        </div>
        <div className={styles.tabletRowMeta}>
          {dateLabel}
          {accountLabel && <> · {accountLabel}</>}
        </div>
      </button>
      <TransactionDialogs
        transaction={transaction}
        replyTime={replyTime}
        displayDescription={displayDescription}
        displayedAmount={displayedAmount}
        displayedResultingBalance={displayedResultingBalance}
        isDialogOpen={isDialogOpen}
        closeDialog={() => setIsDialogOpen(false)}
        onDescriptionUpdate={setDisplayDescription}
        isReportIssueDialogOpen={isReportIssueDialogOpen}
        closeReportIssueDialog={() => setIsReportIssueDialogOpen(false)}
        openReportIssueDialog={() => setIsReportIssueDialogOpen(true)}
      />
    </>
  );
};

// Desktop table row — rendered inside Table.Body
const TransactionRow = ({ transaction, replyTime }: TransactionRowProps) => {
  const { l10n } = useLocalization();
  const {
    isDialogOpen,
    setIsDialogOpen,
    displayDescription,
    setDisplayDescription,
    isReportIssueDialogOpen,
    setIsReportIssueDialogOpen,
    isPositive,
    displayedAmount,
    displayedResultingBalance,
    accountLabel,
    dateLabel,
    isPending,
  } = useTransactionRowState(transaction);

  return (
    <>
      <Table.Row onRowClick={() => setIsDialogOpen(true)}>
        <Table.Cell>{dateLabel}</Table.Cell>
        <Table.Cell>
          <div className={styles.colDescription}>
            <MerchantIcon name={displayDescription} size="s" />
            <div className={styles.descriptionText}>{displayDescription}</div>
            {isPending && (
              <Chip
                label={l10n.getString("transaction-status-pending")}
                kind="secondary"
              />
            )}
          </div>
        </Table.Cell>
        <Table.Cell>{accountLabel}</Table.Cell>
        <Table.Cell textAlign="end">
          <span className={isPositive ? styles.amountPositive : styles.amount}>
            {displayedAmount}
          </span>
        </Table.Cell>
      </Table.Row>
      <TransactionDialogs
        transaction={transaction}
        replyTime={replyTime}
        displayDescription={displayDescription}
        displayedAmount={displayedAmount}
        displayedResultingBalance={displayedResultingBalance}
        isDialogOpen={isDialogOpen}
        closeDialog={() => setIsDialogOpen(false)}
        onDescriptionUpdate={setDisplayDescription}
        isReportIssueDialogOpen={isReportIssueDialogOpen}
        closeReportIssueDialog={() => setIsReportIssueDialogOpen(false)}
        openReportIssueDialog={() => setIsReportIssueDialogOpen(true)}
      />
    </>
  );
};

export default TransactionRow;
